import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Paper, Accordion, AccordionSummary, AccordionDetails, Box, Typography,
  IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Stack, Chip, Divider, Tooltip, ToggleButtonGroup, ToggleButton, Checkbox,
  FormControlLabel, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PreviewIcon from '@mui/icons-material/Preview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useBuild } from '../../contexts/BuildContext';
import HydrationPreviewDrawer from './HydrationPreviewDrawer';

/** The user-facing picker.
 *
 * Each target is just a **named preset** — `user_trades`, `user_stories`,
 * `user_notifications`, `user_price_changes`. The server picks which
 * plugins back the target and balances them (typically 50/50 interesting
 * + convergence, with fill-from-surplus when one side runs short). The
 * picker hides plugin names, share weights, and family schemas entirely.
 *
 * Caller controls: target on/off, per-target limit, per-target drop_empty,
 * and a request-level drop_empty_hits.
 */
const HydrationQueryColumn = () => {
  const { apiCall, hits, hydrationApi, selectedIndex } = useBuild();
  const index = selectedIndex;

  const [mode, setMode] = useState('pipeline');
  const [standaloneIdsText, setStandaloneIdsText] = useState('');

  const [availableTargets, setAvailableTargets] = useState([]);  // [{name, family, plugin_count}]
  const [error, setError] = useState(null);
  const [userBlock, setUserBlock] = useState(null);   // null → use server defaults
  const [dropEmptyHits, setDropEmptyHits] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [running, setRunning] = useState(false);

  const isHydratable = availableTargets.length > 0;
  const isUserModified = userBlock != null;

  // Fetch the named target presets available for this index.
  useEffect(() => {
    let cancelled = false;
    if (!index) return;
    (async () => {
      try {
        const res = await apiCall(`/hydration/targets/${encodeURIComponent(index)}`, {});
        if (cancelled) return;
        setAvailableTargets(Array.isArray(res?.targets) ? res.targets : []);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, index]);

  // Default block: every available target with default limit, no drop_empty.
  const defaultsBlock = useMemo(() => {
    const out = {};
    if (availableTargets.length > 0) {
      // Defaults pick the first target only (typically user_trades) so
      // the picker starts pre-configured with the most useful preset.
      const first = availableTargets[0];
      out[first.name] = { limit: 15, drop_empty: false };
    }
    return out;
  }, [availableTargets]);

  const effectiveBlock = userBlock != null ? userBlock : defaultsBlock;
  const targets = Object.keys(effectiveBlock || {});

  const targetMeta = useMemo(() => {
    const m = {};
    for (const t of availableTargets) m[t.name] = t;
    return m;
  }, [availableTargets]);

  const candidateHits = useMemo(() => (Array.isArray(hits) ? hits : []), [hits]);
  const standaloneIds = useMemo(() => (
    standaloneIdsText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  ), [standaloneIdsText]);
  const hitsToHydrate = useMemo(() => (
    mode === 'standalone'
      ? standaloneIds.map((id) => ({ _index: index, _id: id, _source: { item_id: id } }))
      : candidateHits
  ), [mode, standaloneIds, candidateHits, index]);
  const hitsCount = hitsToHydrate.length;

  // --- spec mutators -------------------------------------------------------

  const handleReset = useCallback(() => setUserBlock(null), []);

  const handleAddTarget = useCallback((targetName) => {
    if (!targetName || !targetMeta[targetName]) return;
    const next = { ...(userBlock || effectiveBlock) };
    if (next[targetName]) return;
    next[targetName] = { limit: 15, drop_empty: false };
    setUserBlock(next);
  }, [effectiveBlock, targetMeta, userBlock]);

  const handleRemoveTarget = useCallback((target) => {
    const next = { ...(userBlock || effectiveBlock) };
    delete next[target];
    setUserBlock(next);
  }, [effectiveBlock, userBlock]);

  const handleLimit = useCallback((target, value) => {
    const n = parseInt(value, 10);
    const next = { ...(userBlock || effectiveBlock) };
    next[target] = { ...next[target], limit: Number.isFinite(n) && n > 0 ? Math.min(n, 50) : 1 };
    setUserBlock(next);
  }, [effectiveBlock, userBlock]);

  const handleDropEmpty = useCallback((target, value) => {
    const next = { ...(userBlock || effectiveBlock) };
    next[target] = { ...next[target], drop_empty: Boolean(value) };
    setUserBlock(next);
  }, [effectiveBlock, userBlock]);

  // --- actions -------------------------------------------------------------

  const handlePreview = useCallback(async () => {
    setError(null);
    try {
      const res = await hydrationApi.describe({ index, hydrate: effectiveBlock });
      setPreviewData(res?.result || res);
      setPreviewOpen(true);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [effectiveBlock, hydrationApi, index]);

  const handleExecute = useCallback(async () => {
    setError(null);
    if (!hitsCount) {
      setError('No hits to hydrate. ' + (mode === 'pipeline'
        ? 'Run a search first or switch to Standalone mode.'
        : 'Paste at least one item_id in the box above.'));
      return;
    }
    setRunning(true);
    try {
      const payload = { index, hits: hitsToHydrate, hydrate: effectiveBlock };
      if (dropEmptyHits) payload.drop_empty_hits = true;
      await hydrationApi.run(payload);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  }, [dropEmptyHits, effectiveBlock, hitsCount, hitsToHydrate, hydrationApi, index, mode]);

  const remainingTargets = useMemo(() => (
    availableTargets.filter((t) => !targets.includes(t.name))
  ), [availableTargets, targets]);

  if (!index) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">Pick an index to configure hydration.</Typography>
      </Paper>
    );
  }
  if (!isHydratable) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Hydration</Typography>
        <Typography variant="body2" color="text.secondary">
          No hydration targets are configured for {index}. Supported indexes:
          polymarket-items, kalshi-items, token-items, hyperliquid-items.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>Hydration</Typography>
          {isUserModified
            ? <Chip size="small" label="custom" color="primary" />
            : <Chip size="small" label="defaults" variant="outlined" />}
        </Stack>

        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          fullWidth
          onChange={(_e, v) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="pipeline">Pipeline ({candidateHits.length} hits)</ToggleButton>
          <ToggleButton value="standalone">Standalone</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'standalone' && (
          <TextField
            label="item_ids (comma- or space-separated)"
            value={standaloneIdsText}
            onChange={(e) => setStandaloneIdsText(e.target.value)}
            multiline minRows={2} maxRows={6} fullWidth size="small"
            sx={{ mb: 2 }}
            helperText={`${standaloneIds.length} ids parsed`}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Tooltip title="Restore the default target set for this index">
            <Button size="small" startIcon={<RestartAltIcon />} onClick={handleReset}>
              Defaults
            </Button>
          </Tooltip>
          {remainingTargets.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Add target</InputLabel>
              <Select
                label="Add target"
                value=""
                onChange={(e) => handleAddTarget(e.target.value)}
              >
                {remainingTargets.map((t) => (
                  <MenuItem key={t.name} value={t.name}>
                    <Stack>
                      <Typography variant="body2">{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.family}{t.plugin_count > 1 ? ` · ${t.plugin_count} sources` : ''}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Show the expected shape per target — no source lookup">
            <Button size="small" startIcon={<PreviewIcon />} onClick={handlePreview}>
              Preview
            </Button>
          </Tooltip>
        </Stack>

        <FormControlLabel
          control={<Checkbox size="small" checked={dropEmptyHits}
                            onChange={(e) => setDropEmptyHits(e.target.checked)} />}
          label="Drop items whose every target is empty"
          sx={{ mb: 1 }}
        />

        <Divider sx={{ mb: 1 }} />

        {targets.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No targets configured. Hydration is a no-op for this call.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {targets.map((target) => {
              const meta = targetMeta[target] || { family: null, plugin_count: 0 };
              const spec = effectiveBlock[target] || {};
              return (
                <Accordion key={target} disableGutters defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      _source.metadata.{target}
                    </Typography>
                    {meta.family && (
                      <Chip size="small" label={meta.family} variant="outlined" />
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small" type="number" label="limit"
                          value={spec.limit ?? 15}
                          sx={{ width: 110 }}
                          inputProps={{ min: 1, max: 50 }}
                          onChange={(e) => handleLimit(target, e.target.value)}
                        />
                        <FormControlLabel
                          control={<Checkbox size="small" checked={Boolean(spec.drop_empty)}
                                            onChange={(e) => handleDropEmpty(target, e.target.checked)} />}
                          label="Drop empty"
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={() => handleRemoveTarget(target)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {meta.plugin_count > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          Auto-balanced across {meta.plugin_count} sources.
                        </Typography>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained" fullWidth startIcon={<PlayArrowIcon />}
            disabled={running || hitsCount === 0}
            onClick={handleExecute}
          >
            {running ? 'Running…' : `Execute hydration (${hitsCount} hits)`}
          </Button>
        </Box>
      </Paper>

      <HydrationPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={previewData}
      />
    </>
  );
};

export default HydrationQueryColumn;
